import { useState, useEffect, useRef, useCallback } from 'react';
import { Form, Field, FieldProps, FormikProvider, useFormik } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import { type AppState, type AppDispatch, Logger, nextgenDebounce } from '@nextgen-web-framework/core';
import {
  useTranslation,
  Input,
  InputTypes,
  Notification,
  Button,
  ButtonStyles,
  ButtonWidth,
  ButtonSize,
  Theme,
  AccountType,
  FAIcon,
  Shade,
  IconStyle,
  Modal,
  useBreakpoint,
  MLPVersion,
  NextgenImage,
  Breakpoints,
} from '@nextgen-web-framework/all';
import {
  profileApi,
  PersonalIdRequestTypeEnum,
  ContactType,
  OwnerType,
  updateRegistrationData,
  MinorDocumentRecordTypeEnum,
  UsernameContactType,
} from '@commerce-profile-webcomponents/sdk';
import type {
  PersonalDetailsType,
  CreateChallengeRequest,
  RegistrationNextHandlerPatchPayload,
  AddressContactType,
  CreateChallengeResponse,
  Gender,
  DynamicObjectInterface,
} from '@commerce-profile-webcomponents/sdk';
import {
  AmwayPasswordInput,
  ComponentType,
  usePasswordValidation,
  UsernameField,
} from '@commerce-identity-webcomponents/ui';
import { OperationType } from '@commerce-identity-webcomponents/sdk';

import AddressInput from '../AddressInput/AddressInput';
import ImageUpload from '../ImageUpload/ImageUpload';
import { inputErrorMessage, inputErrorStatus } from '../Common/InputErrorComponent/utils';
import {
  ApiStatus,
  EDITOR_ASPECT_RATIO,
  ERROR_GENERIC_MESSAGE,
  ErrorConstants,
  TextConstants,
} from '../../utils/Constants';
import { APICallType, IdentityOnInputChangeCallbackResponse } from '../Registration/types/propTypes';
import { useRegistrationFieldsMasterData } from '../../utils/masterdata';
import { extractPhoneNumber, iff, isEmail } from '../../utils/helper';
import {
  addressInputCallbackForRegistration,
  checkUserExists,
  convertToUTCDate,
  getJWTToken,
  getRegistrationValidationData,
  registrationValidationSchema,
  validateDOB,
  sponsorFailedHandler,
  markAllFieldTouched,
  sanitizeInput,
} from '../../utils/registrationUtils';
import { RegistrationValidations } from '../RegistrationIdVerificationStep/types/propTypes';
import SignatureModal from '../SignatureModal/SignatureModal';
import ApiErrorModal from '../ApiErrorModal/ApiErrorModal';
import RegistrationOTPWrapper from '../RegistrationOTPWrapper/RegistrationOTPWrapper';
import GenderInput from '../GenderInput/GenderInput';
import { ApiErrorModalProps } from '../ApiErrorModal/types/propTypes';
import DesktopLayout from '../Common/DesktopLayout/DesktopLayout';
import RegistrationSponsor from '../RegistrationSponsor/RegistrationSponsor';
import { useGlobalErrorHandler } from '../../utils/globalErrorHook';
import logObject from '../../../logObject';
import { AddressInputHandlerType } from '../AddressInput/types/propTypes';
import { RegisterationSponsorRef } from '../RegistrationSponsor/types/propTypes';
import { PatchHandlerType, RegistrationPersonalDetailsStepProps } from './types/propTypes';
import styles from './RegistrationPersonalDetailsStep.module.scss';

const RegistrationPersonalDetailsStep = ({
  nextHandler,
  hideAddressFields = false,
  isSpouse = false,
}: RegistrationPersonalDetailsStepProps) => {
  const logger = Logger.getInstance(
    logObject('RegistrationPersonalDetailsStep.tsx', 'RegistrationPersonalDetailsStep'),
  );
  const t = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const viewPortType = useBreakpoint();
  const [, validatePassword] = usePasswordValidation();
  const apiErrorHandler = useGlobalErrorHandler();
  const isMobile = [Breakpoints.XS, Breakpoints.SM].includes(viewPortType as Breakpoints);
  const [address, setAddress] = useState<AddressContactType>();
  const selectedAccountType: AccountType = useSelector((state: AppState) => state.commerceProfile.selectedAccountType);
  const accountTypeImagesCmsData = useSelector((state: AppState) => state.commerceProfile.accountTypeImagesCMS);
  const personalDetailsImage = accountTypeImagesCmsData?.[selectedAccountType]?.personalDetailsImage;
  const registrationFlowData = useSelector((state: AppState) => state.commerceProfile.registrationFlowData);
  const {
    OtpData,
    perosonalDetails,
    selectSponsor,
    cidVerification,
    userData,
    isChangeUsernameModalOpen,
    flowName,
    currentIndex,
  } = registrationFlowData;
  const {
    isFieldDisabled,
    isAddressSearchFocused,
    firstName = '',
    lastName = '',
    birthDate = '',
    assignAboCheckbox,
    interimPhone,
    citizenshipCountry,
  } = perosonalDetails;
  const registrationCmsData = useSelector((state: AppState) => state.commerceProfile.registrationCmsData);
  const { samplePassport, cidLoading, sampleCid, passportLoading } = registrationCmsData;
  const [isOtpDrawerOpen, setIsOtpDrawerOpen] = useState(false);
  const [challengeId, setChallengeId] = useState<string>('');
  const { patchVersion, registrationId } = userData;
  const { sponsor, internationalSponsor, internationalSponsorCountryCode, setInternationalSponsor } = selectSponsor;
  const { recordType, spouseRecordType } = cidVerification;

  const getRecordType = () => (isSpouse ? spouseRecordType : recordType);
  const isLunar = getRecordType() === PersonalIdRequestTypeEnum.Cid;
  const isForeignerPartner = isSpouse && spouseRecordType === PersonalIdRequestTypeEnum.Passport;
  const [isIdUploaded, setIsIdUploaded] = useState(false);
  const [parentCidImage, setParentCidImage] = useState('');
  const [houseRegistration, setHouseRegistration] = useState('');
  const [modalErrorData, setModalErrorData] = useState<ApiErrorModalProps>();
  const registrationFieldsMasterData = useRegistrationFieldsMasterData();
  const [passwordMeetsMinimum, setPasswordMeetsMinimum] = useState(false);
  const [formErrorNotification, setFormErrorNotification] = useState(false);
  const notificationDiv = useRef<HTMLDivElement>(null);
  const [isSubmitClicked, setIsSubmitClicked] = useState<boolean>(false);
  const registerationSponsorRef = useRef<RegisterationSponsorRef>(null);
  const addressRef = useRef<AddressInputHandlerType>(null);

  //validation schema from master data
  const validationInputs = ['firstName', 'lastName', 'gender', 'birthDate'];

  const getCoAppValidation = (field: DynamicObjectInterface) => {
    if (field?.COAPP) {
      const validations = field?.COAPP?.validations?.[selectedAccountType];

      // for text
      if (validations?.regex && validations?.citizenRegex) {
        // to handle citizen/non-citizen text validation
        const regex = isForeignerPartner ? validations.regex : validations.citizenRegex;
        validations.regex = regex;
      }

      return validations;
    }
    return field?.validations?.[selectedAccountType];
  };
  const getFieldsMasterData = () => {
    const regFieldMasterdata: RegistrationValidations[] = JSON.parse(JSON.stringify(registrationFieldsMasterData));
    if (isSpouse) {
      // Co-applicant - change the config to match standard format with less changes to the validation function
      const coAppFieldNames = [...validationInputs, 'minorAge'];
      regFieldMasterdata?.forEach((field) => {
        if (coAppFieldNames.includes(field?.fieldName as string)) {
          field.validations = {
            ...field.validations,
            [selectedAccountType]: getCoAppValidation(field),
          };
        }
      });
    } else if (selectedAccountType === AccountType.Member && isLunar) {
      regFieldMasterdata?.forEach((field) => {
        if (field.fieldName === 'firstName' && field?.validations?.MEMBER?.regex) {
          field.validations.MEMBER.regex = '^[ก-๙ ]{0,20}$';
        } else if (field.fieldName === 'lastName' && field?.validations?.MEMBER?.regex) {
          field.validations.MEMBER.regex = '^[ก-๙ ]{0,50}$';
        }
      });
    }
    return regFieldMasterdata;
  };

  const validationfields = getRegistrationValidationData({
    config: getFieldsMasterData() as RegistrationValidations[],
    fieldNames: validationInputs,
  });

  const isForeignerSelected = selectedAccountType === AccountType.Member && !isLunar;
  const validationSchema = registrationValidationSchema(
    validationfields,
    selectedAccountType,
    isForeignerSelected || isForeignerPartner,
  );

  const patchHandler = async ({
    payload,
    callOnSuccess,
    imageUpload,
    setShowLoading,
    setUploadWasSuccess,
  }: PatchHandlerType) => {
    const res = await Promise.resolve(nextHandler(payload, undefined, true, callOnSuccess, APICallType.PATCH));
    if (res?.personalId && imageUpload) {
      setShowLoading?.(false);
      setUploadWasSuccess?.(true);
      setShowImageUploadNotification(true);
      setUserIdUploadedSuccessfully(true);
    } else {
      setShowLoading?.(false);
      setUploadWasSuccess?.(false);
    }

    if (res?.responseBody) {
      const { code, status } = res.responseBody;
      errorHandler(code, status);
    }
  };
  const ImageUploadLabel = () => (isLunar ? 'profile.ImageUpload.CIDLabel' : 'profile.ImageUpload.PassportLabel');
  const sponsorExist = sponsor.accountNumber;

  const [passwordError, setPasswordError] = useState('');
  //password component props
  const isPasswordError = !!passwordError;
  const passwordProps = {
    label: t('profile.RegistrationPersonalDetailsStep.password'),
    disabled: isFieldDisabled,
    error: isPasswordError ? t(passwordError) : '',
    setError: setPasswordError,
    forceError: isPasswordError,
  };
  useEffect(() => {
    if (interimPhone) {
      setUsername(interimPhone);
      checkDuplicateUser(interimPhone);
    }
  }, [interimPhone]);
  const interimPhoneObject = extractPhoneNumber(interimPhone);
  //username component state
  const [error, setError] = useState<string>('');
  const [apiError, setApiError] = useState<string>('');
  const [username, setUsername] = useState('');
  const [resetValues, setResetValues] = useState(false);

  const isUsernameError = !!error;
  const usernameProps = {
    dropdownLabel: t('profile.registration.username.countryText'),
    error: isUsernameError ? t(error) : '',
    setError,
    defaultLabel: t('profile.registration.username.smartLabel'),
    mobileLabel: t('profile.registration.username.mobileText'),
    emailLabel: t('profile.registration.username.emailText'),
    checkUsernameConfig: OperationType.UPSERT,
    onInputChangeCallback: async ({ amwayId, hasError }: IdentityOnInputChangeCallbackResponse) => {
      setUsername(amwayId as string);
      if (!hasError && amwayId) {
        await checkDuplicateUserAPIWithDebounce(amwayId, patchVersion);
      }
    },
    disabled: isFieldDisabled,
    resetValues,
    forceError: isUsernameError,
  };

  const validateBirthDate = (date: string, fieldName: string) => {
    setAddAdditionalDocuments(false);
    const dobValidateObj = validateDOB(date, selectedAccountType, getFieldsMasterData(), isLunar, isSpouse, t);
    setBirthDateError(dobValidateObj.msg);
    setFieldTouched(fieldName, true);
    setAddAdditionalDocuments(dobValidateObj.additionalDocument);
  };
  const checkDuplicateUser = async (userName: string, patchVersion?: number) => {
    try {
      const regPayload = {
        usernameContactType: UsernameContactType.Phone,
        phone: userName,
        version: patchVersion,
      };
      const regPatchHandler = () => nextHandler(regPayload, undefined, true, false, APICallType.PATCH);

      const res = await checkUserExists(userName, regPatchHandler);
      if (res?.isUnique == false) {
        if (res?.usageStatus) {
          setError(`profile.RegistrationPersonalDetailsStep.checkUserMessage.${res.usageStatus}`);
          setApiError(`profile.RegistrationPersonalDetailsStep.checkUserMessage.${res.usageStatus}`);
        } else {
          setError('profile.RegistrationPersonalDetailsStep.duplicateUser');
          setApiError('profile.RegistrationPersonalDetailsStep.duplicateUser');
        }
      } else {
        setApiError('');
      }
    } catch (error) {
      setModalErrorData({
        headline: ERROR_GENERIC_MESSAGE.headline,
        body: ERROR_GENERIC_MESSAGE.body,
      });
    }
  };

  const onBlurUsername = () => {
    logger.log(apiError, error, username, 'USERNAME');
    setError(apiError);
  };

  const checkDuplicateUserAPIWithDebounce = nextgenDebounce(checkDuplicateUser, 600);

  //Notification close handler
  const notificationCloseHandler = () => {
    setShowImageUploadNotification(false);
  };

  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showMinorSignatureModal, setShowMinorSignatureModal] = useState(false);
  const [birthDateError, setBirthDateError] = useState('');
  const [addAdditionalDocuments, setAddAdditionalDocuments] = useState(false);

  const showSignature =
    // Not for coapp + member, but show for foreigner
    (isSpouse && isForeignerPartner) ||
    (!isSpouse && selectedAccountType == AccountType.Member && recordType !== PersonalIdRequestTypeEnum.Cid);

  const submitRegistrationdata = async (values: PersonalDetailsType) => {
    const { jwtToken } = userData;

    const payload: RegistrationNextHandlerPatchPayload = {
      givenName: values.firstName,
      familyName: values.lastName,

      identityJWT: jwtToken,
      otpRequestId: challengeId,
      version: patchVersion,
      isRegisteredAsMinor: addAdditionalDocuments,
    };
    if (values.birthDate) {
      payload.birthDate = convertToUTCDate(values.birthDate, isLunar) as unknown as Date;
    }
    if (values.gender) {
      payload.gender = values.gender as Gender;
    }
    if (signatureImage) {
      payload['signature'] = { documentData: signatureImage.split(',')[1] };
    }
    if (sponsor) {
      payload.sponsor = sponsor.accountNumber;
    }
    if (address) {
      payload.billingAddress = { ...address };
    }
    if (addAdditionalDocuments) {
      payload.minorDocuments = [
        {
          recordType: MinorDocumentRecordTypeEnum.ParentCid,
          documentData: parentCidImage,
        },
        {
          recordType: MinorDocumentRecordTypeEnum.HouseRegistration,
          documentData: houseRegistration,
        },
        {
          recordType: MinorDocumentRecordTypeEnum.ParentSign,
          documentData: minorDocumentSign.split(',')[1],
        },
      ];
    }
    if (assignAboCheckbox) {
      payload.doNotKnowSponsor = true;
    }

    //add multiBusiness flag and internationalSponsor support.
    if (Object.keys(internationalSponsor).length !== 0) {
      payload.internationalSponsor = {
        accountNumber: internationalSponsor.accountNumber,
        countryCode: internationalSponsorCountryCode,
      };
    }

    //.multipleBusinessFlag = internationalSponsorType === InternationalSponsorTypeEnum.MULTIPLE_BUSINESS;

    const callOnSuccess = true;
    const res = await Promise.resolve(nextHandler(payload, undefined, false, callOnSuccess));
    if (res?.responseBody) {
      const { code, status } = res.responseBody;
      errorHandler(code, status);
    }
  };
  const uploadImageHandler = useCallback(
    (image: string, setShowLoading?: Function, setUploadWasSuccess?: Function) => {
      const imageBase64 = image.split(',');
      const payload = {
        personalId: { documentData: imageBase64[1] },
        version: patchVersion,
      };
      const imageUpload = true;
      patchHandler({ payload, imageUpload, setShowLoading, setUploadWasSuccess });
      setIsIdUploaded(true);
      setShowLoading?.(true);
    },
    [patchVersion],
  );
  const parentCidImageHandler = useCallback(
    (image: string, _setShowLoading?: Function, setUploadWasSuccess?: Function) => {
      const imageBase64 = image.split(',');
      setParentCidImage(imageBase64[1]);
      setUploadWasSuccess?.(true);
    },
    [],
  );

  const primaryModalButtonDefaultCallback = useCallback(() => setModalErrorData(undefined), []);

  const houseRegistrationImageHandler = useCallback(
    (image: string, _setShowLoading?: Function, setUploadWasSuccess?: Function) => {
      const imageBase64 = image.split(',');
      setHouseRegistration(imageBase64[1]);
      setUploadWasSuccess?.(true);
    },
    [],
  );

  useEffect(() => {
    // This part will overwrite the input values. Split into 2 useEffects
    setFieldValue('firstName', firstName);
    setFieldValue('lastName', lastName);
    setFieldValue('birthDate', birthDate);
  }, [firstName, lastName, birthDate]);

  useEffect(() => {
    !isFieldDisabled && birthDate && validateBirthDate(birthDate, 'birthDate');
  }, [birthDate, isFieldDisabled]);

  const initiateOtpHandler = async () => {
    setResetValues(false);
    disableFields(true);
    const inputType = isEmail(username) ? 'email' : 'phone';
    const password = values.createPassword ?? '';
    const token = await getJWTToken(username, password, apiErrorHandler);
    const updatedData = {
      ...registrationFlowData,
      initialRegistrationForm: {
        ...registrationFlowData.initialRegistrationForm,
        inputForm: {
          ...values,
          userName: username,
        },
      },
      OtpData: {
        ...OtpData,
        inputType,
      },
      userData: {
        ...userData,
        jwtToken: token,
      },
      perosonalDetails: {
        ...perosonalDetails,
        isFieldDisabled: true,
      },
    };
    // call challenge api
    const payload: CreateChallengeRequest = {
      [inputType]: username,
    };

    try {
      if (registrationId) {
        const challengeRes = await apiErrorHandler<CreateChallengeResponse>(() =>
          profileApi.postRegistrationChallenge({
            payload,
            registrationId,
          }),
        );

        setChallengeId(challengeRes?.challengeId ?? '');
        if (challengeRes?.version) {
          updatedData.userData.patchVersion = challengeRes.version;
        }
      }
      dispatch(updateRegistrationData(updatedData));
      openDrawer();
    } catch (error) {
      setModalErrorData({
        headline: ERROR_GENERIC_MESSAGE.headline,
        body: ERROR_GENERIC_MESSAGE.headline,
      });
    }
  };

  const formik = useFormik({
    initialValues: perosonalDetails,
    onSubmit: onFormSubmitClickHandler, //submitRegistrationdata,
    validationSchema: validationSchema,
    validateOnBlur: true,
  });

  function onFormSubmitClickHandler() {
    const isFormValid = !!enableVerifyOtpCheck();
    logger.log(
      'here',
      'enableVerifyOtpCheck',
      isFormValid,
      'error',
      errors,
      'values',
      values,
      'isPasswordValid',
      isPasswordValid,
      'isUsernameValid',
      isUsernameValid,
      'validateAddress',
      validateAddress,
      'isSponsorValid',
      isSponsorValid,
      'birthDateError',
      birthDateError,
      'validAccountType',
      validAccountType(),
      'isFieldDisabled',
      !isFieldDisabled,
      'isDocumentValid',
      isDocumentValid,
      'isForeignerPartnerSignValid',
      isForeignerPartnerSignValid,
      'isErrorFree',
      isErrorFree,
    );

    setIsSubmitClicked(true);
    if (isFormValid) {
      initiateOtpHandler();
      setFormErrorNotification(false);
    } else {
      setFormErrorNotification(true);
      addressRef?.current?.validate();
      registerationSponsorRef?.current?.validateSponsor();
      formik.setErrors({ ...errors });
      formik.setTouched(markAllFieldTouched(values), true);

      if (!isUsernameValid && !error) {
        setError('profile.validationMsg.username.required');
      }
      if (!isPasswordValid && !values.createPassword) {
        setPasswordError('profile.validationMsg.password.required');
      }
      notificationDiv?.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }
  }

  const { values, handleChange, handleBlur, setFieldValue, errors, setFieldTouched } = formik;
  useEffect(() => {
    if (values.createPassword) {
      const isValidPassword = validatePassword(values?.createPassword);
      setPasswordValid(isValidPassword);
    }
  }, [values.createPassword]);
  const [signatureDone, setSignatureDone] = useState(false);
  const [signatureCleared, setSignatureCleared] = useState(false);
  const [signatureEmpty, setSignatureEmpty] = useState(true);
  const [signatureImage, setSignatureImage] = useState(String);

  // For minor document signature
  const [minorSignatureDone, setMinorSignatureDone] = useState(false);
  const [minorSignatureCleared, setMinorSignatureCleared] = useState(false);
  const [minorSignatureEmpty, setMinorSignatureEmpty] = useState(true);
  const [minorDocumentSign, setMinorDocumentSign] = useState(String);
  //const [signatureTouched, setSignatureTouched] = useState(false);
  //loading image states
  const [showImageUploadNotification, setShowImageUploadNotification] = useState(false);
  const [userIdUploadedSuccessfully, setUserIdUploadedSuccessfully] = useState(false);
  const [passwordValid, setPasswordValid] = useState(false);
  const [sponsorFailed, setSponsorFailed] = useState<boolean>(false);
  const { NEXT_PUBLIC_IDENTITY_ALLOW_PASSWORD_STRENGTH_METER: allowPasswordStrengthMeter } = ConfigService.getConfigs();
  const validateAddress = address?.feValidation || hideAddressFields;
  const validateSponsor = sponsorExist || assignAboCheckbox;
  const validAccountType = () =>
    selectedAccountType !== AccountType.Member ||
    (selectedAccountType === AccountType.Member && (signatureImage || recordType === 'CID')) ||
    isSpouse;

  const isUsernameLengthValid = username.length >= 4;
  const isUsernameErrorFree = error.length === 0;
  const isUsernameValid = username && isUsernameLengthValid && isUsernameErrorFree;
  const isPasswordValid = (!allowPasswordStrengthMeter && passwordValid) || passwordMeetsMinimum;
  const isSponsorValid = validateSponsor || isSpouse;
  const documentFlag = parentCidImage && minorSignatureDone && houseRegistration;
  const isDocumentValid = iff(addAdditionalDocuments, Boolean(documentFlag), true);
  const isForeignerPartnerSignValid = iff(isForeignerPartner, signatureDone, true);
  const isErrorFree = error.length === 0 && Object.entries(errors).length === 0;
  const enableVerifyOtpCheck = () =>
    values.firstName &&
    values.lastName &&
    (values.gender || isForeignerPartner) &&
    values.birthDate &&
    isPasswordValid &&
    isUsernameValid &&
    isErrorFree &&
    validateAddress &&
    isSponsorValid &&
    birthDateError.length === 0 &&
    validAccountType() &&
    !isFieldDisabled &&
    isDocumentValid &&
    isForeignerPartnerSignValid;

  const openDrawer = () => {
    setIsOtpDrawerOpen(true);
  };

  const disableFields = (value: boolean) => {
    const updatedData = {
      ...registrationFlowData,

      perosonalDetails: {
        ...perosonalDetails,
        isFieldDisabled: value,
      },
    };
    dispatch(updateRegistrationData(updatedData));
  };
  const onCloseDrawer = () => {
    const updatedData = {
      ...registrationFlowData,
      perosonalDetails: {
        ...perosonalDetails,
        isFieldDisabled: false,
        birthDate: values?.birthDate,
      },
    };

    dispatch(updateRegistrationData(updatedData));
    setIsOtpDrawerOpen(false);
  };

  const onSignatureConfirm = function () {
    setSignatureDone(true);
  };
  const onMinorSignatureConfirm = function () {
    setMinorSignatureDone(true);
  };

  const onSignatureClear = function () {
    if (!isFieldDisabled) {
      setSignatureCleared((prev) => !prev);
      setSignatureEmpty(true);
      setSignatureImage('');
      setSignatureDone(false);
    }
  };
  const onMinorSignatureClear = function () {
    if (!isFieldDisabled) {
      setMinorSignatureCleared((prev) => !prev);
      setMinorSignatureEmpty(true);
      setMinorDocumentSign('');
      setMinorSignatureDone(false);
    }
  };

  const onSignatureTouched = () => {
    setSignatureEmpty(false);
  };
  const onMinorSignatureTouched = () => {
    setMinorSignatureEmpty(false);
  };

  const saveSignature = function (imageData: string) {
    //store to redux here.
    setSignatureImage(imageData);
    setShowSignatureModal(false);
  };
  const saveMinorSignature = function (imageData: string) {
    setMinorDocumentSign(imageData);
    setShowMinorSignatureModal(false);
  };

  const onSignatureClose = () => {
    setShowSignatureModal(false);
  };
  const onMinorSignatureClose = () => {
    setShowMinorSignatureModal(false);
  };

  const onUsernameChangeHandler = () => {
    setIsOtpDrawerOpen(false);
  };

  const otpArgs = {
    nextHandler: function () {
      submitRegistrationdata(values);
    },
    username: username,
    challengeId,
    //TODO change signature to accept submitRegistrationdata
    onCloseDrawer,
    onUsernameChangeHandler,
  };

  const cidExpiredHandler = () => {
    dispatch(
      updateRegistrationData({
        ...registrationFlowData,
        currentIndex: currentIndex - 1,
      }),
    );
    primaryModalButtonDefaultCallback();
  };

  const handleSponsorFailedAndUpdateState = () =>
    sponsorFailedHandler(flowName, () => {
      dispatch(
        updateRegistrationData({
          selectSponsor: {
            ...selectSponsor,
            sponsor: {},
            editSponsor: true,
          },
        }),
      );
      setSponsorFailed(true);
      primaryModalButtonDefaultCallback();
    });

  const errorHandler = (code: string, status: number) => {
    const invalidSponsorError = code === ErrorConstants.SPONSOR_FAILED;
    const cidExpiredError = code === ErrorConstants.CID_EXPIRED_CODE;
    const buttonLabel = invalidSponsorError || cidExpiredError ? `profile.ApiErrorModal.${code}.button` : undefined;
    if (status === 400) {
      if (code.includes(ApiStatus.BAD_REQUEST)) {
        setModalErrorData({
          headline: ERROR_GENERIC_MESSAGE.headline,
          body: ERROR_GENERIC_MESSAGE.body,
        });
      } else {
        const errorCallback = () => {
          dispatch(
            updateRegistrationData({
              ...registrationFlowData,
              clearUserIdImage: true,
            }),
          );
          primaryModalButtonDefaultCallback();
        };

        setModalErrorData({
          headline: `profile.ApiErrorModal.${code}.headline`,
          body: `profile.ApiErrorModal.${code}.body`,
          primaryButtonCallback: invalidSponsorError
            ? handleSponsorFailedAndUpdateState
            : cidExpiredError
            ? cidExpiredHandler
            : errorCallback,
          primaryButtonLabel: buttonLabel ?? '',
        });
      }
    } else {
      setModalErrorData({
        headline: ERROR_GENERIC_MESSAGE.headline,
        body: ERROR_GENERIC_MESSAGE.body,
      });
    }
  };

  useEffect(() => {
    if (showImageUploadNotification) {
      setTimeout(notificationCloseHandler, 3000);
    }
  }, [showImageUploadNotification]);

  const displaySigntuare = (heading: string, image: string, isMinor = false) => {
    const showSignatureModalHandler = (isMinor = false) => {
      isMinor ? setShowMinorSignatureModal(true) : setShowSignatureModal(true);
    };
    const removeSignatureHandler = iff(isMinor, onMinorSignatureClear, onSignatureClear);

    const isSignatureDone = iff(isMinor, minorSignatureDone, signatureDone);

    return (
      <div className={styles['signature-parent']}>
        <div className={`${styles['signature-title']} ${!isFieldDisabled ? styles['active-title'] : ''}`}>
          {heading}
        </div>
        <div className={styles['signature-completed']}>
          {isSignatureDone && (
            <>
              <div className={styles['signature-image']}>
                <NextgenImage src={image} alt="Signature" />
              </div>
              {removeIcon(removeSignatureHandler)}
            </>
          )}
        </div>
        <div>
          {!isSignatureDone && (
            <div
              className={`${styles['signature']} ${isFieldDisabled ? styles['disable-signature'] : ''}`}
              data-testid="signature"
              tabIndex={-1}
              aria-hidden="true"
              onClick={() => {
                !isFieldDisabled ? showSignatureModalHandler(isMinor) : '';
              }}
            >
              <div className={`${styles['action-icon']} ${isFieldDisabled ? styles['disable-icon'] : ''}`}>
                <FAIcon
                  theme={isFieldDisabled ? Theme.GRAY : Theme.PURPLE}
                  iconName={'pen'}
                  shade={Shade.DARK}
                  iconStyle={IconStyle.REGULAR}
                  className={styles['icon']}
                />
              </div>
              <div className={`${styles['signature-label']} ${isFieldDisabled ? styles['disable-label'] : ''}`}>
                {t('profile.RegistrationPersonalDetailsStep.signaction')}
              </div>
            </div>
          )}
        </div>
        {!isSignatureDone && isSubmitClicked && errorMessage(signatureErrorLabel)}
      </div>
    );
  };
  const additionDocumentsNotification = () => (
    <>
      {addAdditionalDocuments && (
        <div className={styles['registration-notification-container']}>
          <Notification
            message=""
            description={t('profile.RegistrationPersonalDetailsStep.AboDateWarning')}
            theme={Theme.WARNING}
            hasNotificationIcon={true}
            data-testid="test-notification-id"
          />
        </div>
      )}
    </>
  );
  const houseRegistrationUploaded = houseRegistration.length > 1;
  const parentCidUploaded = parentCidImage.length > 1;
  const removeIcon = (callback: () => void) => (
    <div
      className={`${styles['remove-icon-container']} ${isFieldDisabled ? styles['disabled-remove'] : ''} `}
      data-testid="remove-icon-container"
      tabIndex={-1}
      aria-hidden="true"
      onClick={callback}
    >
      <div className={styles['remove-icon']}>
        <FAIcon
          theme={isFieldDisabled ? Theme.GRAY : Theme.PURPLE}
          iconName={'xmark-large'}
          shade={Shade.DARK}
          iconStyle={IconStyle.REGULAR}
          className={styles['icon']}
        />
      </div>
      <div className={styles['remove-label']}>{t('profile.RegistrationPersonalDetailsStep.removeSign')}</div>
    </div>
  );
  const getImageForUserId = (showImageLoading: boolean) => {
    if (isLunar) {
      if (showImageLoading) {
        return cidLoading;
      } else {
        return sampleCid;
      }
    } else {
      if (showImageLoading) {
        return passportLoading;
      } else {
        return samplePassport;
      }
    }
  };
  const removePassportHandler = useCallback(() => {
    setUserIdUploadedSuccessfully(false);
    setShowImageUploadNotification(false);
    dispatch(
      updateRegistrationData({
        ...registrationFlowData,
        clearUserIdImage: true,
      }),
    );
    setIsIdUploaded(false);
    disableFields(true);
  }, [registrationFlowData]);
  const showRemoveIconForPassport = () => !isLunar && userIdUploadedSuccessfully;
  const updateGender = (value: string) => {
    setFieldValue('gender', value);
  };
  const appendSlashHandler = async (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(e);
    const { value, selectionStart } = e.target;
    let inputValue = e.target.value.replace(/\D/g, ''); // Remove non-digit characters
    if (inputValue.length > 8) {
      inputValue = inputValue.slice(0, 8);
    }
    // Format the input value with '/' after every 2 characters
    let formattedValue = '';
    if (inputValue.length > 0) {
      formattedValue += inputValue.slice(0, 2); // First 2 characters
    }
    if (inputValue.length > 2) {
      formattedValue += `/${inputValue.slice(2, 4)}`; // Next 2 characters
    }
    if (inputValue.length > 4) {
      formattedValue += `/${inputValue.slice(4, 8)}`; // Last 4 characters
    }

    await setFieldValue('birthDate', formattedValue);
    let newCursorPosition = selectionStart || 0;
    // Adjust cursor position to account for slashes being added or removed
    const previousValue = value;
    if (previousValue.length < formattedValue.length && (newCursorPosition === 3 || newCursorPosition === 6)) {
      newCursorPosition += 1;
    } else if (previousValue.length > formattedValue.length && (newCursorPosition === 3 || newCursorPosition === 6)) {
      newCursorPosition -= 1;
    }
    e.target.setSelectionRange(newCursorPosition, newCursorPosition);
  };

  const updateChangeUsernameModal = () => {
    dispatch(
      updateRegistrationData({
        ...registrationFlowData,
        perosonalDetails: {
          ...perosonalDetails,
          isFieldDisabled: false,
        },
        isChangeUsernameModalOpen: false,
      }),
    );
  };
  const handleChangeUsernameSecondaryButton = () => {
    updateChangeUsernameModal();
    setIsOtpDrawerOpen(true);
  };

  const handlerUsernamePrimaryButton = () => {
    disableFields(false);
    updateChangeUsernameModal();
    setIsOtpDrawerOpen(false);
    setUsername('');
    setResetValues(true);
  };

  const usernameRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (usernameRef.current && usernameRef.current.scrollIntoView) {
      usernameRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [usernameRef]);

  const getBirthDateMessage = (meta: DynamicObjectInterface) => {
    if ((!birthDate || !meta.value) && meta?.error) {
      return inputErrorMessage(t, meta.touched, meta.error);
    } else if (birthDateError.length > 1) {
      return birthDateError;
    }
    return '';
  };

  const errorMessage = (message: string, wrapperClass?: string) => (
    <span
      role="presentation"
      aria-label="message"
      className={`${styles['float-input-message']} ${wrapperClass}`}
      data-testid="message-test-id"
    >
      {<FAIcon iconName={'square-exclamation'} theme={Theme.RED} className={styles['input-icon']} />}
      {<span className={styles['float-message']}>{t(message)} </span>}
    </span>
  );

  const signatureErrorLabel = iff(
    isForeignerPartner || isForeignerSelected,
    'profile.validationMsg.yourSignature.required',
    'profile.validationMsg.signature.required',
  );

  const renderGenderInput = () => (
    <div className={`${styles['gender-field']} ${iff(!isLunar, styles['remove-margin-top'], '')}`}>
      <Field
        type="text"
        name="gender"
        component={GenderInput}
        isSubmitClicked={isSubmitClicked}
        value={values.gender}
        updateGenderCallback={updateGender}
        disabled={isFieldDisabled}
      />
    </div>
  );

  const renderPasswordInput = () => (
    <div className={styles['password-input-field']}>
      <AmwayPasswordInput
        name="createPassword"
        mlpVersion={MLPVersion.ONE}
        {...passwordProps}
        onInputChangeCallback={(e) => {
          handleChange(e);
          setPasswordError('');
        }}
        onPasswordMeetsMinimumChange={(meetsMinimum) => {
          setPasswordMeetsMinimum(meetsMinimum);
        }}
        component={ComponentType.WITHOUT_CONFIRM}
        required={false}
      />
    </div>
  );

  return (
    <DesktopLayout desktopImage={personalDetailsImage} imageClassName={styles['desktop-image']}>
      <div
        className={styles['registration-personal-details-wrapper']}
        data-testid="registration-personal-details-wrapper"
      >
        <div
          className={styles['registration-personal-details-conatiner']}
          data-testid="registration-personal-details-conatiner"
        >
          <div
            className={styles['registration-personal-details-heading']}
            data-testid="registration-personal-details-heading"
          >
            {t('profile.RegistrationPersonalDetailsStep.heading')}
          </div>

          {isSubmitClicked && !isIdUploaded && (
            <div className={styles['doc-notification-bar']}>
              <Notification
                description=""
                message={t('profile.RegistrationPersonalDetailsStep.documentError')}
                hasBoldText={false}
                textLink={{
                  href: '',
                  text: '',
                }}
                theme={Theme.ERROR}
                customIcon={{
                  className: styles['notification-icon'],
                }}
              />
            </div>
          )}

          <div className={styles['image-upload']} data-testid="image-upload">
            <ImageUpload
              uploadLabel={iff(
                showRemoveIconForPassport(),
                t('profile.ImageUpload.uploadLabel'),
                t(`${ImageUploadLabel()}`),
              )}
              callback={uploadImageHandler}
              showImage={true}
              wrapperClass={styles['upload-cid-wrapper']}
              imageClass={styles['upload-cid-image']}
              canRemoveAfterSuccess={!isLunar}
              sampleImage={getImageForUserId(false)}
              loadingImage={getImageForUserId(true)}
              removeIconHandler={removePassportHandler}
              testId="image0"
              editorAspectRatio={EDITOR_ASPECT_RATIO.CID}
            />
          </div>

          <div className={styles['input-form']} data-testid="input-form" ref={notificationDiv}>
            <FormikProvider value={formik}>
              {formErrorNotification && (
                <div className={styles['notification-bar']}>
                  <Notification
                    description=""
                    message={t('profile.RegistrationPersonalDetailsStep.errorHeading')}
                    hasBoldText={false}
                    onCloseClick={() => setFormErrorNotification(false)}
                    textLink={{
                      href: '',
                      text: '',
                    }}
                    theme={Theme.ERROR}
                  />
                </div>
              )}

              <Form>
                <div
                  className={`${styles['input-form-wrapper']} ${iff(hideAddressFields, styles['co-app-ui'], '')}`}
                  data-testid="input-form-wrapper"
                >
                  <div className={styles['name-wrapper']} data-testid="name-wrapper">
                    <div className={styles['input-fname-field']} data-testid="input-fname-field">
                      <Field type="text" name="firstName">
                        {({ meta }: FieldProps) => (
                          <Input
                            id="firstName"
                            data-testid="firstName"
                            type={InputTypes.TEXT}
                            name="firstName"
                            value={values.firstName}
                            label={t('profile.RegistrationPersonalDetailsStep.firstName')}
                            onChange={(e) => {
                              const input = sanitizeInput(e.target.value);
                              setFieldValue('firstName', input);
                            }}
                            onBlur={handleBlur}
                            mlpVersion={MLPVersion.TWO}
                            disabled={isFieldDisabled || !isIdUploaded}
                            status={inputErrorStatus(meta.touched, meta.error)}
                            message={inputErrorMessage(t, meta.touched, meta.error, {
                              max: TextConstants.NAME_MAX,
                            })}
                            maxLength={20}
                          />
                        )}
                      </Field>
                    </div>

                    <div className={styles['input-lname-field']}>
                      <Field type="text" name="lastName">
                        {({ meta }: FieldProps) => (
                          <Input
                            id="lastName"
                            data-testid="lastName"
                            type={InputTypes.TEXT}
                            name="lastName"
                            label={t('profile.RegistrationPersonalDetailsStep.lastName')}
                            onChange={(e) => {
                              const val = sanitizeInput(e.target.value);
                              setFieldValue('lastName', val);
                            }}
                            value={values.lastName}
                            onBlur={handleBlur}
                            mlpVersion={MLPVersion.TWO}
                            disabled={isFieldDisabled || !isIdUploaded}
                            status={inputErrorStatus(meta.touched, meta.error)}
                            message={inputErrorMessage(t, meta.touched, meta.error, {
                              max: TextConstants.NAME_MAX,
                            })}
                            maxLength={50}
                          />
                        )}
                      </Field>
                    </div>
                  </div>
                  <div className={styles['birth-date-gender-wrapper']} data-testid="birth-date-gender-wrapper">
                    {isForeignerPartner && (
                      <div
                        className={styles['citizenship-field']}
                        data-testid="citizenship-input"
                        tabIndex={-1}
                        aria-hidden="true"
                      >
                        <Input
                          id="citizenship"
                          data-testid="citizenship"
                          type={InputTypes.TEXT}
                          name="citizenship"
                          value={iff(
                            citizenshipCountry,
                            t(`profile.RegistrationIdVerificationStep.country.${citizenshipCountry}`),
                            '',
                          )}
                          label={t('profile.RegistrationIdVerificationStep.citizenLabel')}
                          maxLength={50}
                          readOnly
                          inputIcon={{ iconName: 'chevron-down', iconStyle: IconStyle.SOLID }}
                          disabled
                        />
                      </div>
                    )}

                    <div
                      className={`${styles['date-field']} ${iff(
                        isMobile && addAdditionalDocuments,
                        styles['remove-margin-bottom'],
                        '',
                      )}`}
                    >
                      <Field type="text" name="birthDate">
                        {({ meta }: FieldProps) => (
                          <>
                            <Input
                              id="birthDate"
                              data-testid="birthDate"
                              name="birthDate"
                              label={t('profile.RegistrationPersonalDetailsStep.birthDate')}
                              onChange={(e) => {
                                appendSlashHandler(e);
                              }}
                              value={values.birthDate}
                              onBlur={(e) => {
                                handleBlur(e);

                                validateBirthDate(values.birthDate as string, 'birthDate');
                              }}
                              disabled={isFieldDisabled}
                              isLunarFormatted={true}
                              placeholder="DD/MM/YYYY"
                              mlpVersion={MLPVersion.TWO}
                              status={inputErrorStatus(
                                meta.touched,
                                !birthDate || !values.birthDate ? meta?.error ?? birthDateError : birthDateError,
                              )}
                              message={getBirthDateMessage(meta)}
                            />

                            {isLunar && (
                              <div
                                className={`${styles['date-text']} ${
                                  isFieldDisabled ? styles['disabled'] : styles['active']
                                }`}
                              >
                                {t('profile.RegistrationPersonalDetailsStep.birthDateInfo')}
                              </div>
                            )}
                          </>
                        )}
                      </Field>
                    </div>
                    {isMobile && additionDocumentsNotification()}
                    {!isForeignerPartner && renderGenderInput()}
                  </div>

                  {!isMobile && additionDocumentsNotification()}

                  <div className={styles['password-phone-wrapper']} data-testid="password-phone-wrapper">
                    {isForeignerPartner && renderGenderInput()}
                    <div className={styles['phone-input-field']} ref={iff(resetValues, usernameRef, null)}>
                      <UsernameField
                        {...usernameProps}
                        onBlur={onBlurUsername}
                        key={interimPhone}
                        autoPopulateValue={interimPhoneObject?.number}
                      />
                      <span
                        className={`${styles['verification-message']} ${iff(
                          isFieldDisabled,
                          styles['disabled'],
                          styles['active'],
                        )}`}
                      >
                        {t('profile.RegistrationPersonalDetailsStep.phoneNote')}
                      </span>
                    </div>
                    {!isForeignerPartner && renderPasswordInput()}
                  </div>

                  {isForeignerPartner && (
                    <div className={styles['password-phone-wrapper']} data-testid="password2-phone-wrapper">
                      {renderPasswordInput()}
                      <div className={styles['dummy-input-field']}></div>
                    </div>
                  )}
                </div>

                <div className={`${styles['address-container']}${iff(isAddressSearchFocused, styles['focused'], '')}`}>
                  {!hideAddressFields && (
                    <div className={`${styles['address-input-field']} ${iff(!isFieldDisabled, styles['enabled'], '')}`}>
                      <AddressInput
                        title={t('profile.RegistrationPersonalDetailsStep.primaryAddress')}
                        hideConfirmButton={true}
                        callback={(contact) => {
                          addressInputCallbackForRegistration(contact, () => {
                            setAddress(contact);
                            dispatch(
                              updateRegistrationData({
                                perosonalDetails: {
                                  ...perosonalDetails,
                                  isAddressSearchFocused: false,
                                },
                              }),
                            );
                          });
                        }}
                        accountNumber={''}
                        contactType={ContactType.Mailing}
                        ownerType={OwnerType.Account}
                        disabled={isFieldDisabled}
                        useAvailableWidth={!isMobile}
                        ref={addressRef}
                        // validateOnMount={isSubmitClicked}
                      />
                    </div>
                  )}

                  {isSpouse && showSignature && (
                    <div className={styles['signature-wrapper']}>
                      {displaySigntuare(t('profile.RegistrationPersonalDetailsStep.ownsignature'), signatureImage)}
                    </div>
                  )}

                  {addAdditionalDocuments && (
                    <div
                      className={`${styles['additional-documents']} ${isSpouse ? styles['top-border'] : ''}`}
                      data-testid="additional-documents"
                    >
                      <div className={styles['image-conatiner']} data-testid="image-container">
                        <div className={styles['parent-cid-upload']} data-testid="parent-cid-upload">
                          <div className={styles['heading']} data-testid="parent-cid-upload-heading">
                            {t('profile.RegistrationPersonalDetailsStep.parentCidLabel')}
                          </div>

                          <ImageUpload
                            uploadLabel={t('profile.RegistrationPersonalDetailsStep.imageLabel')}
                            callback={parentCidImageHandler}
                            showImage={parentCidUploaded}
                            uploadClass={styles['upload-cta-position']}
                            imageClass={styles['upload-additional-image']}
                            canRemoveAfterSuccess={true}
                            testId="image1"
                            editorAspectRatio={EDITOR_ASPECT_RATIO.CID}
                            removeIconHandler={() => setParentCidImage('')}
                          />
                          {isSubmitClicked &&
                            !parentCidUploaded &&
                            errorMessage('profile.validationMsg.parentCid.required')}
                        </div>
                        <div className={styles['house-reg-upload']} data-testid="house-reg-upload">
                          <div className={styles['heading']} data-testid="house-reg-upload-heading">
                            {t('profile.RegistrationPersonalDetailsStep.houseRegLabel')}
                          </div>
                          <ImageUpload
                            uploadLabel={t('profile.RegistrationPersonalDetailsStep.imageLabel')}
                            callback={houseRegistrationImageHandler}
                            showImage={houseRegistrationUploaded}
                            uploadClass={styles['upload-cta-position']}
                            imageClass={styles['upload-additional-image']}
                            canRemoveAfterSuccess={true}
                            testId="image2"
                            editorAspectRatio={EDITOR_ASPECT_RATIO.HOUSE_REGISTRATION}
                            removeIconHandler={() => setHouseRegistration('')}
                          />
                          {isSubmitClicked &&
                            !houseRegistrationUploaded &&
                            errorMessage('profile.validationMsg.houseRegistration.required')}
                        </div>
                      </div>
                      <div className={styles['signature-conatiner']} data-testid="signature-conatiner">
                        {displaySigntuare(
                          t('profile.RegistrationPersonalDetailsStep.parentsignature'),
                          minorDocumentSign,
                          true,
                        )}
                      </div>
                    </div>
                  )}
                  {!hideAddressFields && (
                    <div
                      className={`${styles['sponsor-wrapper']}  ${iff(
                        setInternationalSponsor,
                        styles['full-width'],
                        '',
                      )}`}
                    >
                      <RegistrationSponsor
                        isFieldDisabled={isFieldDisabled}
                        showABOSelected={false}
                        isFocused={sponsorFailed}
                        ref={registerationSponsorRef}
                      />
                    </div>
                  )}

                  {!isSpouse && showSignature && (
                    <div className={styles['signature-wrapper']}>
                      {displaySigntuare(t('profile.RegistrationPersonalDetailsStep.ownsignature'), signatureImage)}
                    </div>
                  )}
                </div>

                <div className={styles['verification-btn']}>
                  <Button
                    title={t('profile.RegistrationPersonalDetailsStep.verificationButton')}
                    onClick={onFormSubmitClickHandler}
                    data-testid="verification-btn-testid"
                    buttonStyle={ButtonStyles.SOLID}
                    size={ButtonSize.MEDIUM}
                    disabled={isFieldDisabled}
                    buttonWidth={ButtonWidth.FULL_WIDTH}
                  />
                </div>
                {isOtpDrawerOpen && <RegistrationOTPWrapper {...otpArgs} />}
              </Form>
            </FormikProvider>
          </div>

          {showSignatureModal && (
            <SignatureModal
              signatureEmpty={signatureEmpty}
              onSignatureConfirm={onSignatureConfirm}
              onSignatureClear={onSignatureClear}
              onSignatureClose={onSignatureClose}
              signatureDone={signatureDone}
              signatureCleared={signatureCleared}
              onSignatureTouched={onSignatureTouched}
              saveSignature={saveSignature}
            />
          )}
          {showMinorSignatureModal && (
            <SignatureModal
              signatureEmpty={minorSignatureEmpty}
              onSignatureConfirm={onMinorSignatureConfirm}
              onSignatureClear={onMinorSignatureClear}
              onSignatureClose={onMinorSignatureClose}
              signatureDone={minorSignatureDone}
              signatureCleared={minorSignatureCleared}
              onSignatureTouched={onMinorSignatureTouched}
              saveSignature={saveMinorSignature}
            />
          )}
        </div>

        {isChangeUsernameModalOpen && (
          <Modal
            headline={t('profile.RegistrationChangeUsernameModal.headline')}
            primaryButtonLabel={t('profile.RegistrationChangeUsernameModal.primaryButtonMobile')}
            secondaryButtonLabel={t('profile.RegistrationChangeUsernameModal.secondaryButtonMobile')}
            onSecondaryButtonClick={handleChangeUsernameSecondaryButton}
            onPrimaryButtonClick={handlerUsernamePrimaryButton}
            onClose={handleChangeUsernameSecondaryButton}
          >
            <span data-testid="changeEmail-username-test-id">
              {t('profile.RegistrationChangeUsernameModal.modalTextMobile')}
            </span>
          </Modal>
        )}

        {showImageUploadNotification && (
          <div className={styles['notification-container']}>
            <Notification
              description={iff(isLunar, t('profile.RegistrationPersonalDetailsStep.notification.message'), '')}
              message={t('profile.RegistrationPersonalDetailsStep.notification.heading')}
              theme={Theme.SUCCESS}
              hasBoldText={true}
              hasNotificationIcon={true}
              onCloseClick={notificationCloseHandler}
              data-testid="notification-test-id"
            />
          </div>
        )}
        {(modalErrorData?.headline || modalErrorData?.body) && (
          <ApiErrorModal
            {...modalErrorData}
            primaryButtonCallback={modalErrorData?.primaryButtonCallback ?? primaryModalButtonDefaultCallback}
          />
        )}
      </div>
    </DesktopLayout>
  );
};
export default RegistrationPersonalDetailsStep;
